<template>
  <auth-wrapper>
    <form
      class="auth"
      @submit.prevent="signIn"
    >
      <div class="auth-block__data">
        <div
          v-if="isEmailVerified === null && processing && !user"
          class="alert alert-info py-4 d-flex align-items-center justify-content-center"
        >
          <b class="mr-2">{{ $t('common.please_wait') }}</b>
          <b-spinner type="grow" />
        </div>
        <div
          v-else-if="isEmailVerified && user"
          class="alert alert-info pt-4 pb-4 text-center"
        >
          <b>{{ $t('common.user') }}: {{ user.email }}</b><br>
          <b>{{ $t('views.email_verification.token_validated') }}!</b>
        </div>
        <div
          v-else-if="isEmailVerified === false"
          class="alert alert-warning"
        >
          {{ $t('views.email_verification.token_invalid') }}
        </div>
        <template v-if="isEmailVerified">
          <div class="input-block">
            <label
              class="input-block__label"
              for="password1"
            >
              {{ $t('common.create_password') }}
              <span
                v-if="validation.password.error"
                class="error-text"
              >
                {{ validation.password.error }}
              </span>
            </label>
            <input
              id="password1"
              v-model="password"
              class="input-block__input"
              :class="{'input-block__input_error': validation.password.error}"
              autocomplete="new-password"
              type="password"
              @input="resetValidation"
            >
          </div>
          <div class="input-block">
            <label
              class="input-block__label"
              for="password2"
            >
              {{ $t('common.confirm_password') }}
              <span
                v-if="validation.confirmPassword.error"
                class="error-text"
              >
                {{ validation.confirmPassword.error }}
              </span>
            </label>
            <input
              id="password2"
              v-model="confirmPassword"
              class="input-block__input"
              :class="{'input-block__input_error': validation.confirmPassword.error}"
              autocomplete="new-password"
              type="password"
              @input="resetValidation"
            >
          </div>
        </template>
      </div>
      <div
        v-if="isEmailVerified"
        class="actions-block"
      >
        <button
          :disabled="false"
          class="btn spso-button spso-button_line spso-button_filled_white-color"
          type="submit"
        >
          {{ $t('common.set_password') }}
        </button>
      </div>
      <div
        v-else
        class="forget"
      >
        <router-link
          class="forget__text"
          :to="{name: 'ResetPassword'}"
          tag="span"
        >
          {{ $t('views.email_verification.reset_password') }}
        </router-link>
      </div>
    </form>
  </auth-wrapper>
</template>

<script>
import { mapState } from 'vuex';
import AuthWrapper from '@/components/auth/AuthWrapper.vue';

export default {
  name: 'EmailVerification',
  components: {
    AuthWrapper,
  },
  props: {
    verificationCode: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isEmailVerified: null,
    processing: false,
    password: '',
    confirmPassword: '',
    validation: {
      password: { error: '', state: null },
      confirmPassword: { error: '', state: null },
    },
  }),
  computed: {
    ...mapState(['user']),
  },
  created() {
    this.$store.commit('signOut');
    setTimeout(() => {
      this.verifyEmail();
    }, 100);
  },
  methods: {
    async verifyEmail() {
      this.processing = true;

      try {
        await this.$store.dispatch('getBackendToken', this.verificationCode);

        this.isEmailVerified = true;

        // Get user data if token received
        await this.$store.dispatch('performAuth', {
          jwtacc: localStorage.getItem('jwtacc'),
          refreshToken: localStorage.getItem('refreshToken'),
          locale: this.$i18n.locale,
          app: this,
        });
      } catch (error) {
        this.isEmailVerified = false;
        // Show verify email token error
        this.$toast(error.message, {
          title: this.$t('toast.danger'),
          variant: 'danger',
          solid: true,
          autoHideDelay: 15000,
        });
      } finally {
        this.processing = false;
      }
    },
    validateForm() {
      this.resetValidation();

      let isFormValid = true;

      if (this.password && this.password.length < 8) {
        this.validation.password.error = this.$t('validation.password_length_8');
        this.validation.password.state = false;
        isFormValid = false;
      }

      if (!this.password) {
        this.validation.password.error = this.$t('validation.required');
        this.validation.password.state = false;
        isFormValid = false;
      }

      if (!this.confirmPassword) {
        this.validation.confirmPassword.error = this.$t('validation.required');
        this.validation.confirmPassword.state = false;
        isFormValid = false;
      }

      if (this.password !== this.confirmPassword) {
        this.validation.confirmPassword.error = this.$t('validation.password_mismatch');
        this.validation.confirmPassword.state = false;
        isFormValid = false;
      }

      return isFormValid;
    },
    async signIn() {
      if (this.validateForm()) {
        this.processing = true;
        const params = {
          password: this.password,
        };

        try {
          await this.$http.put(`users/${this.user.id}`, params);
          this.$router.push('/users');
        } catch (err) {
          if (err.response && err.response.data) {
            this.validation.password.error = err.response.data.message;
          } else {
            this.$http.mockApiError(err);
          }
        }

        this.processing = false;
      }
    },
    resetValidation() {
      this.validation.password.error = '';
      this.validation.confirmPassword.error = '';
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
